// src/pages/IntakePage.js
//
// Ticket 061 (Advisor Intake I4), widened by ticket 064 (I5). The /intake page,
// behind the browser flag REACT_APP_INTAKE_ENABLED (see App.js). Welcome, Section 1
// (eight screens, the last is filing history), an email-code gate, "continue where
// you left off", and after the gate "Your money" (events, income, accounts), each
// Next saving that screen through intake-save (intakeSession.runSaveScreen), then
// the Review screen (ticket 066).
//
// ONE STABLE COMPONENT TREE (CLAUDE.md rule 12): the reducer's `stage` picks what
// renders INSIDE IntakeShell. useAuth().user is never used to choose a wrapper or
// swap a component type; it only decides whether the code step is skipped.
//
// NOTHING IS STORED BEFORE THE CODE PASSES: Section 1 lives in React state and a
// tab-only sessionStorage copy (intakeDraft.js). After the code, the already
// deployed intake-start / intake-save / intake-load functions store the answers
// (intakeApi.js). The page does not use redirectAfterSignIn (it would hard
// navigate and drop in-memory state); advisor accounts are handled by the
// server's 403 PARTNER_ACCOUNT.
//
// ADVISOR MODE (ticket 073): the same page with a fixed `mode="advisor"` prop, rendered by
// AdvisorIntakePage at /advisor/intake with the client the advisor owns (`subjectId`) and an
// `onLeave` that returns to the client list. A route prop, never state (rule 12), so the tree is
// the same stable one. Differences, all conditional JSX/effects inside it: no referral capture or
// partner lookup, no tab draft, no Welcome and no email code (the advisor is signed in; the page
// opens the client straight away), every intake call carries subjectId, and the share step is
// never reached (shareStatusState starts 'unavailable', so Results show no share entry).

import React, { useEffect, useReducer, useRef, useState } from 'react'
import { useAuth } from '../context/AuthContext'
import IntakeShell from '../components/intake/IntakeShell'
import IntakeWelcome from '../components/intake/IntakeWelcome'
import IntakeQuestionScreen from '../components/intake/IntakeQuestionScreen'
import IntakeSection23Screen from '../components/intake/IntakeSection23Screen'
import IntakeGate from '../components/intake/IntakeGate'
import IntakeReview from '../components/intake/IntakeReview'
import IntakeResults from '../components/intake/IntakeResults'
import IntakeShare from '../components/intake/IntakeShare'
import IntakeMessage from '../components/intake/IntakeMessage'
import { captureReferralIfPresent, getActivePartnerRef } from '../lib/partnerAttribution'
import { fetchPartnerNameByRef } from '../lib/partnerLookup'
import { initialState, reduce, screenIndex, isLastScreen } from '../lib/intake/intakeFlow'
import {
  SECTION1_SCREEN_ORDER, SECTION_NAMES, sectionOfScreen, REVIEW_SCREEN, RESULTS_SCREEN, RESULTS_STEP_LABEL,
  RESULTS_LOADING, RESULTS_REVIEW_LABEL, RESULTS_FAILED_TITLE, RESULTS_FAILED_BODY, RESULTS_RETRY_LABEL,
  SHARE_STEP_LABEL, ADVISOR_PICK_TITLE, ADVISOR_START_ANOTHER_YEAR, ADVISOR_RESUMING,
} from '../lib/intake/intakeConfig'
import { intakeTaxYearOptions } from '../lib/intake/intakeYears'
import { isScreenComplete } from '../lib/intake/section1Facts'
import { buildReview } from '../lib/intake/reviewModel'
import { buildReviewAnswers } from '../lib/intake/reviewAnswers'
import { buildResultsView } from '../lib/intake/resultsModel'
import { isScreenComplete23, visibleScreens, isCardScreen, untouchedCount, CARD_SCREENS } from '../lib/intake/section23Facts'
import {
  runSubmit, runResume, runSaveScreen, runCalculate, runSaveResults, runShareStatus, runShare, runStopSharing,
} from '../lib/intake/intakeSession'
import { loadDraft, saveDraft, clearDraft } from '../lib/intake/intakeDraft'
import {
  trackAdvisorClientOpened,
  trackWelcomeViewed, trackScreenViewed, trackGateReached, trackGatePassed,
  trackResumeRequested, trackResumed, trackSaveFailed,
  trackResultsViewed, trackResultsSaved, trackCalculateFailed, trackResultsTabViewed,
  trackShareViewed, trackShared, trackShareFailed, trackShareStopped,
} from '../lib/intake/intakeAnalytics'

const SECTION_LABEL = SECTION_NAMES[1]
const sectionLabel = (n) => SECTION_NAMES[n]

const FAILURE_TEXT = {
  NETWORK: 'We could not reach the server. Your answers are still here. Check your connection and try again.',
  DEFAULT: 'Something went wrong. Your answers are still here. Please try again.',
}

export default function IntakePage({ mode = 'client', subjectId, onLeave }) {
  const advisor = mode === 'advisor'
  // Sent on every intake call in advisor mode only; a client never sends one.
  const sid = advisor ? subjectId : undefined
  const { user, session, signOut } = useAuth()
  const [state, dispatch] = useReducer(reduce, undefined, () => initialState(advisor ? null : loadDraft(), { advisor }))
  const [partnerRef, setPartnerRef] = useState(null)
  const [partnerName, setPartnerName] = useState(null)
  const stateRef = useRef(state)
  // tokenRef is only the token from the code step, which expires after about an hour.
  // The live session token (signedInToken, kept fresh by Supabase) is always tried first.
  const tokenRef = useRef(null)
  stateRef.current = state
  const today = useRef(new Date()).current
  const signedInToken = user && session ? session.access_token : null

  // Referral: /intake is outside AppShell, so capture it here, once.
  useEffect(() => {
    if (advisor) return
    captureReferralIfPresent()
    setPartnerRef(getActivePartnerRef() || null)
  }, [advisor])

  useEffect(() => {
    if (!partnerRef) return undefined
    let cancelled = false
    fetchPartnerNameByRef(partnerRef).then(({ data }) => {
      if (!cancelled && data && data.name) setPartnerName(data.name)
    })
    return () => { cancelled = true }
  }, [partnerRef])

  // Tab-only copy of Section 1 while signed out (D-OD2).
  useEffect(() => {
    if (!advisor && !user && (state.stage === 'section1' || state.stage === 'gate')) {
      saveDraft({ answers: state.answers, taxYear: state.taxYear, screen: state.screen })
    }
  }, [advisor, user, state.stage, state.answers, state.taxYear, state.screen])

  // Analytics.
  useEffect(() => {
    if (state.stage === 'welcome') trackWelcomeViewed(!!partnerRef)
  }, [state.stage, partnerRef])
  useEffect(() => {
    if (state.stage === 'section1' || state.stage === 'section23') trackScreenViewed(state.screen)
    if (state.stage === 'review') trackScreenViewed(REVIEW_SCREEN)
    if (state.stage === 'results') trackScreenViewed(RESULTS_SCREEN)
    if (state.stage === 'share') trackShareViewed()
    if (state.stage === 'gate') trackGateReached()
  }, [state.stage, state.screen])

  const yearOptions = intakeTaxYearOptions(today, state.answers.move)

  async function submit(accessToken) {
    tokenRef.current = accessToken
    dispatch({ type: 'submitting' })
    const action = await runSubmit({ accessToken, state: stateRef.current, partnerRef, subjectId: sid })
    if (action.type === 'failed') trackSaveFailed(action.code)
    dispatch({ ...action, today })
  }

  async function resume(accessToken) {
    tokenRef.current = accessToken
    dispatch({ type: 'resuming' })
    const action = await runResume({ accessToken, years: intakeTaxYearOptions(today, null), subjectId: sid })
    if (action.type === 'failed') trackSaveFailed(action.code)
    if (action.type === 'candidates' && action.candidates.length === 1) trackResumed()
    dispatch({ ...action, today })
  }

  // Section 2 or 3: one intake-save per Next, then the reducer moves on. A failed
  // save keeps the person on the screen (screenSaveFailed).
  async function saveScreen() {
    const token = signedInToken || tokenRef.current
    if (!token) return
    dispatch({ type: 'screenSaving' })
    const action = await runSaveScreen({ accessToken: token, state: stateRef.current, subjectId: sid })
    if (action.type === 'screenSaveFailed') trackSaveFailed(action.code)
    dispatch({ ...action, today })
  }

  // Results: one calculation per revision. The reducer resets resultsStatus to idle
  // whenever the revision changes; the ref stops a second call for the same
  // revision and attempt (React strict mode runs effects twice in development).
  const calcKey = useRef(null)
  const attempt = useRef(0)
  useEffect(() => {
    if (state.stage !== 'results' || state.resultsStatus !== 'idle' || state.revision == null) return
    const token = signedInToken || tokenRef.current
    if (!token) return
    const key = `${state.revision}:${attempt.current}`
    if (calcKey.current === key) return
    calcKey.current = key
    dispatch({ type: 'resultsLoading' })
    runCalculate({ accessToken: token, taxYear: state.taxYear, subjectId: sid }).then((action) => {
      if (action.type === 'resultsFailed') trackCalculateFailed(action.code)
      dispatch({ ...action, today })
    })
  }, [state.stage, state.resultsStatus, state.revision, state.taxYear, signedInToken, today, sid])

  const viewedKey = useRef(null)
  useEffect(() => {
    if (state.stage !== 'results' || state.resultsStatus !== 'ready' || !state.results) return
    const key = state.results.meta && state.results.meta.facts_revision
    if (viewedKey.current === key) return
    viewedKey.current = key
    const r = state.results
    trackResultsViewed(!!r.irpf, ((r.completeness && r.completeness.open_facts) || []).length > 0)
  }, [state.stage, state.resultsStatus, state.results])

  // Share status (ticket 070): read once Results are ready, and again after a Save while
  // sharing. A failure (no linked advisor, function not deployed, network) means no share
  // entry; Results are never affected. The ref stops a second call in React strict mode.
  const shareInFlight = useRef(false)
  useEffect(() => {
    if (state.stage !== 'results' || state.resultsStatus !== 'ready' || state.shareStatusState !== 'idle') return
    const token = signedInToken || tokenRef.current
    if (!token || state.taxYear == null || shareInFlight.current) return
    shareInFlight.current = true
    dispatch({ type: 'shareStatusLoading' })
    runShareStatus({ accessToken: token, taxYear: state.taxYear }).then((action) => {
      shareInFlight.current = false
      dispatch(action)
    })
  }, [state.stage, state.resultsStatus, state.shareStatusState, state.taxYear, signedInToken])

  async function shareResults(contact) {
    const token = signedInToken || tokenRef.current
    const current = stateRef.current
    if (!token || current.shareSending || !current.results) return
    dispatch({ type: 'shareSending' })
    const action = await runShare({
      accessToken: token, taxYear: current.taxYear, factsRevision: current.results.meta.facts_revision, contact,
    })
    if (action.type === 'shareDone') trackShared()
    if (action.type === 'shareFailed') trackShareFailed(action.code)
    dispatch({ ...action, today })
  }

  async function stopSharingResults() {
    const token = signedInToken || tokenRef.current
    const current = stateRef.current
    if (!token || current.shareSending) return
    dispatch({ type: 'shareSending' })
    const action = await runStopSharing({ accessToken: token, taxYear: current.taxYear })
    if (action.type === 'stopDone') trackShareStopped()
    if (action.type === 'shareFailed') trackShareFailed(action.code)
    dispatch({ ...action, today })
  }

  function retryResults() {
    attempt.current += 1
    dispatch({ type: 'resultsRetry' })
  }

  async function saveResults() {
    const token = signedInToken || tokenRef.current
    const results = stateRef.current.results
    if (!token || !results || state.resultsSaving) return
    dispatch({ type: 'resultsSaving' })
    const action = await runSaveResults({
      accessToken: token, taxYear: stateRef.current.taxYear, baseRevision: results.meta.facts_revision, subjectId: sid,
    })
    if (action.type === 'resultsSaved') trackResultsSaved()
    if (action.type === 'saveResultsFailed') trackSaveFailed(action.code)
    dispatch({ ...action, today })
  }

  function advanceSection23() {
    // Empty cards are removed first, with a notice; the next Next saves.
    if (isCardScreen(state.screen)) {
      const cfg = CARD_SCREENS[state.screen]
      if (untouchedCount(state.answers, cfg.owner, cfg.groups) > 0) {
        dispatch({ type: 'dropUntouched' })
        return
      }
    }
    if (state.screen === '3.4') { dispatch({ type: 'next' }); return }
    saveScreen()
  }

  // Advisor mode: open the client as soon as the page has the signed-in token. Once.
  const opened = useRef(false)
  useEffect(() => {
    if (!advisor || opened.current || !signedInToken || !sid) return
    opened.current = true
    trackAdvisorClientOpened()
    resume(signedInToken)
  }, [advisor, signedInToken, sid])

  function handleGateSuccess(newSession) {
    trackGatePassed()
    clearDraft()
    submit(newSession.access_token)
  }

  function handleResumeGateSuccess(newSession) {
    trackGatePassed()
    resume(newSession.access_token)
  }

  function handleStart() {
    clearDraft()
    dispatch({ type: 'begin' })
  }

  function handleContinue() {
    trackResumeRequested()
    dispatch({ type: 'continue', signedIn: !!signedInToken })
    if (signedInToken) resume(signedInToken)
  }

  function advanceFrom() {
    // A Fix/Edit of any Section 1 screen saves Section 1 as a whole, right away.
    const last = isLastScreen(state.screen) || state.returnToReview
    const token = signedInToken || tokenRef.current
    if (last && token) {
      submit(token)
      return
    }
    dispatch({ type: 'next', today })
  }

  function handleRetry() {
    const token = signedInToken || tokenRef.current
    if (!token) { dispatch({ type: 'back' }); return }
    if (state.failure && state.failure.where === 'resume') resume(token)
    else submit(token)
  }

  async function handleSignOut() {
    await signOut()
    clearDraft()
    tokenRef.current = null
    dispatch({ type: 'restart' })
  }

  const canNext = state.stage === 'section1'
    && isScreenComplete(state.screen, { answers: state.answers, taxYear: state.taxYear, yearOptions, today })

  const ctx23 = { answers: state.answers, suggestions: state.suggestions, dismissed: state.dismissed }
  const canNext23 = state.stage === 'section23' && isScreenComplete23(state.screen, ctx23)

  // ── What renders inside the shell ──────────────────────────────────────────
  let stepLabel = ''
  let activeSegment = 0
  let onBack = null
  let content = null

  switch (state.stage) {
    case 'welcome':
      activeSegment = -1
      content = <IntakeWelcome partnerName={partnerName} onStart={handleStart} onContinue={handleContinue} />
      break

    case 'section1': {
      const idx = screenIndex(state.screen)
      stepLabel = `${SECTION_LABEL} · Step ${idx + 1} of ${SECTION1_SCREEN_ORDER.length}`
      onBack = () => dispatch({ type: 'back' })
      const last = isLastScreen(state.screen)
      content = (
        <IntakeQuestionScreen
          screenId={state.screen}
          answers={state.answers}
          taxYear={state.taxYear}
          yearOptions={yearOptions}
          today={today}
          onMove={(patch) => dispatch({ type: 'setMove', patch, today })}
          onFact={(key, fact) => dispatch({ type: 'setFact', key, fact })}
          onClearFact={(key) => dispatch({ type: 'clearFact', key })}
          onTaxYear={(year) => dispatch({ type: 'setTaxYear', year })}
          onFiling={(patch) => dispatch({ type: 'setFiling', patch })}
          onNext={() => advanceFrom()}
          canNext={canNext}
          nextLabel={state.returnToReview ? 'Save' : last ? (signedInToken ? 'Save my answers' : 'Next: confirm your email') : 'Next'}
          notice={state.notice}
          busy={false}
        />
      )
      break
    }

    case 'section23': {
      const section = sectionOfScreen(state.screen)
      const inSection = visibleScreens(ctx23).filter((id) => sectionOfScreen(id) === section)
      const idx = Math.max(0, inSection.indexOf(state.screen))
      stepLabel = `${sectionLabel(section)} · Step ${idx + 1} of ${inSection.length}`
      activeSegment = section - 1
      onBack = () => dispatch({ type: 'back' })
      content = (
        <IntakeSection23Screen
          screenId={state.screen}
          state={state}
          dispatch={dispatch}
          canNext={canNext23}
          onNext={advanceSection23}
          nextLabel={state.screen === '3.4' ? 'Continue' : 'Next'}
        />
      )
      break
    }

    case 'gate':
      stepLabel = `${SECTION_LABEL} · Confirm your email`
      onBack = () => dispatch({ type: 'back' })
      content = (
        <IntakeGate
          title="Confirm your email to save your answers"
          body="We will email you a 6-digit code. Once you enter it, your answers so far are saved and you can continue from any device."
          submitLabel="Save my answers"
          onSuccess={handleGateSuccess}
        />
      )
      break

    case 'resume-gate':
      stepLabel = 'Continue where you left off'
      activeSegment = -1
      onBack = () => dispatch({ type: 'back' })
      content = (
        <IntakeGate
          title="Continue where you left off"
          body="Enter the email you used before. We will send you a 6-digit code."
          submitLabel="Continue"
          onSuccess={handleResumeGateSuccess}
        />
      )
      break

    case 'saving':
      stepLabel = `${SECTION_LABEL} · Saving`
      content = <IntakeMessage title="Saving your answers…" busy />
      break

    case 'resuming':
      stepLabel = 'Continue where you left off'
      activeSegment = -1
      content = <IntakeMessage title={advisor ? ADVISOR_RESUMING : 'Looking for your saved answers…'} busy />
      break

    case 'results': {
      stepLabel = RESULTS_STEP_LABEL
      activeSegment = 1
      onBack = () => dispatch({ type: 'back' })
      if (state.resultsStatus === 'ready' && state.results) {
        const comunidadFact = state.answers.facts && state.answers.facts.comunidad_autonoma
        const view = buildResultsView({
          results: state.results,
          ctx: ctx23,
          comunidad: comunidadFact && comunidadFact.status === 'known_value' ? comunidadFact.value : null,
          savedRevision: state.savedRevision,
          saving: state.resultsSaving,
        })
        content = (
          <>
            {state.notice && <p className="intake-notice">{state.notice}</p>}
            <IntakeResults
              results={state.results}
              view={view}
              saving={state.resultsSaving}
              saveError={state.saveResultsError}
              share={state.shareStatus ? {
                advisorName: state.shareStatus.advisorName,
                shared: state.shareStatus.shared,
                sharedAt: state.shareStatus.sharedAt,
                onOpen: () => dispatch({ type: 'openShare' }),
              } : null}
              onSave={saveResults}
              onReview={() => dispatch({ type: 'openReview' })}
              onTab={trackResultsTabViewed}
              onFix={(screen) => dispatch({ type: 'editScreen', screen, from: 'results' })}
            />
          </>
        )
      } else if (state.resultsStatus === 'failed') {
        content = (
          <IntakeMessage
            title={RESULTS_FAILED_TITLE}
            body={RESULTS_FAILED_BODY}
            primary={{ label: RESULTS_RETRY_LABEL, onClick: retryResults }}
            secondary={{ label: RESULTS_REVIEW_LABEL, onClick: () => dispatch({ type: 'openReview' }) }}
          />
        )
      } else {
        content = <IntakeMessage title={RESULTS_LOADING} busy />
      }
      break
    }

    case 'share':
      stepLabel = SHARE_STEP_LABEL
      activeSegment = 1
      onBack = () => dispatch({ type: 'back' })
      content = state.shareStatus ? (
        <IntakeShare
          status={state.shareStatus}
          email={user ? user.email : ''}
          sending={state.shareSending}
          error={state.shareError}
          onShare={shareResults}
          onStop={stopSharingResults}
          onBack={() => dispatch({ type: 'back' })}
        />
      ) : null
      break

    case 'review':
      stepLabel = 'Review'
      activeSegment = 1
      onBack = () => dispatch({ type: 'back' })
      content = (
        <IntakeReview
          review={buildReview({ answers: state.answers, taxYear: state.taxYear, today, suggestions: state.suggestions, dismissed: state.dismissed })}
          rows={buildReviewAnswers({ answers: state.answers, taxYear: state.taxYear, today, suggestions: state.suggestions, dismissed: state.dismissed })}
          notice={state.notice}
          onOpen={(screen) => dispatch({ type: 'editScreen', screen })}
          onResults={() => dispatch({ type: 'showResults' })}
        />
      )
      break

    case 'resume-pick':
      stepLabel = 'Continue where you left off'
      activeSegment = -1
      content = (
        <div className="intake-holding">
          <h1 className="triage-question">{advisor ? ADVISOR_PICK_TITLE : 'Which tax year do you want to continue?'}</h1>
          <div className="u-stack-sm">
            {state.candidates.map((c) => (
              <button
                key={c.taxYear}
                type="button"
                className="triage-option"
                onClick={() => { trackResumed(); dispatch({ type: 'pickCandidate', taxYear: c.taxYear, today }) }}
              >
                {c.taxYear}
              </button>
            ))}
            {advisor && (
              <button type="button" className="btn-secondary" onClick={() => dispatch({ type: 'startAnotherYear' })}>
                {ADVISOR_START_ANOTHER_YEAR}
              </button>
            )}
          </div>
        </div>
      )
      break

    case 'resume-none':
      stepLabel = 'Continue where you left off'
      activeSegment = -1
      content = (
        <IntakeMessage
          title="We could not find a saved intake for that email"
          body="You can start a new one. Your answers are saved once you confirm your email."
          primary={{ label: 'Start a new intake', onClick: handleStart }}
          secondary={{ label: 'Back', onClick: () => dispatch({ type: 'restart' }) }}
        />
      )
      break

    case 'failed':
      stepLabel = state.failure && state.failure.where === 'resume' ? 'Continue where you left off' : `${SECTION_LABEL} · Not saved`
      onBack = () => dispatch({ type: 'back' })
      content = (
        <IntakeMessage
          title="That did not work"
          body={FAILURE_TEXT[state.failure && state.failure.code] || FAILURE_TEXT.DEFAULT}
          primary={{ label: 'Try again', onClick: handleRetry }}
        />
      )
      break

    case 'advisor':
      activeSegment = -1
      content = (
        <IntakeMessage
          title="Advisor accounts cannot fill this in yet"
          body="You are signed in with an advisor account. Sign out to complete the questionnaire as a client."
          primary={{ label: 'Sign out', onClick: handleSignOut }}
        />
      )
      break

    case 'unavailable':
      activeSegment = -1
      content = (
        <IntakeMessage
          title="This questionnaire is not available right now"
          body="Please try again later."
        />
      )
      break

    default:
      content = null
  }

  // Advisor mode: where the client flow would go back to the Welcome, the advisor goes back to
  // the client list. Nothing saved is lost; a later "Open" resumes from the stored answers.
  if (advisor && onLeave) {
    const atFirstQuestion = state.stage === 'section1' && screenIndex(state.screen) <= 0 && !state.returnToReview
    const resumeFailed = state.stage === 'failed' && state.failure && state.failure.where === 'resume'
    if (state.stage === 'resume-pick' || atFirstQuestion || resumeFailed) onBack = onLeave
  }

  return (
    <IntakeShell stepLabel={stepLabel} activeSegment={activeSegment} onBack={onBack} wide={state.stage === 'results' && state.resultsStatus === 'ready'}>
      {content}
    </IntakeShell>
  )
}
